<template>
  <div class="technical-page">
    <!-- Header Section -->
    <section class="technical-header">
      <div class="container">
        <div class="header-content animate__animated animate__fadeInUp">
          <div class="security-badge">
            <i class="fas fa-shield-alt"/>
            <span>AUTHORIZED ACCESS ONLY</span>
          </div>
          <h1>Technical Architecture</h1>
          <p>Confidential infrastructure and implementation details</p>
          <div class="user-info">
            <i class="fas fa-user-shield"/>
            <span>Authenticated as: {{ currentUser?.username }}</span>
            <span v-if="currentUser?.demo" class="demo-indicator">
              <i class="fas fa-flask"/>
              Demo Mode
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Infrastructure Foundation -->
    <section class="infrastructure-section">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2><i class="fas fa-server"/> Infrastructure Foundation</h2>
          <p>Enterprise-grade VMware and Kubernetes infrastructure serving critical applications</p>
        </div>

        <div class="infrastructure-grid">
          <div class="infra-card animate__animated animate__fadeInLeft">
            <div class="card-header">
              <i class="fas fa-cloud"/>
              <h3>VMware vSphere Infrastructure</h3>
              <span class="classification">CLASSIFIED</span>
            </div>
            <div class="card-content">
              <ul class="spec-list">
                <li><strong>Management Cluster:</strong> 3 ESXi hosts with enterprise redundancy</li>
                <li><strong>Compute Clusters:</strong> 6 hosts for standard workloads</li>
                <li><strong>GPU Cluster:</strong> 4 hosts with NVIDIA A100 for AI workloads</li>
                <li><strong>Storage:</strong> VMware vSAN with NVMe SSD arrays</li>
                <li><strong>Network:</strong> Multi-VLAN architecture with 10Gb backbone</li>
              </ul>
            </div>
          </div>

          <div class="infra-card animate__animated animate__fadeInRight">
            <div class="card-header">
              <i class="fas fa-dharmachakra"/>
              <h3>Kubernetes HA Templates</h3>
              <span class="classification">CLASSIFIED</span>
            </div>
            <div class="card-content">
              <ul class="spec-list">
                <li><strong>Control Plane:</strong> 3-node HA configuration</li>
                <li><strong>Worker Nodes:</strong> Auto-scaling based on demand</li>
                <li><strong>Network Plugin:</strong> Calico with network policies</li>
                <li><strong>Storage:</strong> Dynamic provisioning with multiple classes</li>
                <li><strong>Monitoring:</strong> Prometheus + Grafana + Alertmanager</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Network Topology -->
        <div class="network-topology animate__animated animate__fadeInUp">
          <h3><i class="fas fa-network-wired"/> Network Architecture</h3>
          <div class="topology-diagram">
            <div class="vlan-group">
              <div class="vlan-card">
                <strong>VLAN 15</strong>
                <span>vMotion Network</span>
                <code>10.15.0.0/24</code>
              </div>
              <div class="vlan-card">
                <strong>VLAN 20</strong>
                <span>Storage Network</span>
                <code>10.20.0.0/24</code>
              </div>
              <div class="vlan-card">
                <strong>VLAN 30</strong>
                <span>Server/VM Network</span>
                <code>10.30.0.0/24</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Platform Services -->
    <!-- Complete DenvStack Architecture — moved here from the homepage
         (design doc §8: architecture vocabulary lives on /technical). -->
    <section class="architecture-section">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2><i class="fas fa-diagram-project"/> Complete DenvStack Architecture</h2>
          <p>How the layers fit together, from the AI core to the cluster</p>
        </div>

        <div class="tech-icons-row">
          <div class="tech-icon-item" v-for="(icon, name) in icons" :key="name">
            <img :src="icon" :alt="name" class="tech-icon-img">
            <span class="tech-icon-label">{{ name }}</span>
          </div>
        </div>

        <div class="arch-card">
          <div class="arch-image">
            <img :src="architectureImage" alt="DenvStack platform architecture" class="arch-img">
          </div>
          <div class="arch-content">
            <p>
              The platform integrates AI at the core to orchestrate the whole infrastructure.
              From backend APIs to databases, from LLM integration to DevOps automation, DenvStack
              coordinates every layer so operations are consistent and observable.
            </p>
            <div class="arch-breakdown">
              <div class="breakdown-section">
                <h4><i class="fas fa-brain"/> AI core</h4>
                <p>Deployment analysis, auto-remediation, command correction and secret masking</p>
              </div>
              <div class="breakdown-section">
                <h4><i class="fas fa-code-branch"/> Backend layer</h4>
                <p>Python Flask APIs (auth, cluster, DNS, backup) and Node.js services sharing one library</p>
              </div>
              <div class="breakdown-section">
                <h4><i class="fas fa-database"/> Data layer</h4>
                <p>MongoDB for workloads, MySQL for users, PostgreSQL support with scheduled backups</p>
              </div>
              <div class="breakdown-section">
                <h4><i class="fas fa-rocket"/> Infrastructure</h4>
                <p>Containers, Kubernetes orchestration, HAProxy load balancing, GitLab CI/CD</p>
              </div>
            </div>
            <ul class="arch-feature-list">
              <li><i class="fas fa-check-circle"/> Automated Kubernetes cluster management</li>
              <li><i class="fas fa-check-circle"/> AI-powered deployment analysis and auto-remediation</li>
              <li><i class="fas fa-check-circle"/> Multi-tenant infrastructure as a service</li>
              <li><i class="fas fa-check-circle"/> Real-time workload orchestration</li>
              <li><i class="fas fa-check-circle"/> LLM integration for intelligent operations</li>
              <li><i class="fas fa-check-circle"/> Command correction with secret masking</li>
            </ul>
          </div>
        </div>

        <!-- Service suite, condensed (the six homepage cards folded in) -->
        <div class="suite-grid">
          <article v-for="svc in serviceSuite" :key="svc.title" class="suite-card">
            <i :class="svc.icon"/>
            <h3>{{ svc.title }}</h3>
            <p>{{ svc.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- Platform Services -->
    <section class="platform-services">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2><i class="fas fa-cogs"/> Platform Services</h2>
          <p>Production-ready microservices architecture with proven scalability</p>
        </div>

        <div class="services-grid">
          <div class="service-card animate__animated animate__fadeInUp">
            <div class="service-header">
              <i class="fab fa-node-js"/>
              <h3>API Gateway</h3>
            </div>
            <div class="service-content">
              <p><strong>Technology:</strong> Express.js + MongoDB cluster</p>
              <p><strong>Features:</strong> JWT authentication, rate limiting, request logging</p>
              <p><strong>Scaling:</strong> Horizontal scaling with load balancing</p>
            </div>
          </div>

          <div class="service-card animate__animated animate__fadeInUp">
            <div class="service-header">
              <i class="fas fa-exchange-alt"/>
              <h3>Message Queue</h3>
            </div>
            <div class="service-content">
              <p><strong>Technology:</strong> RabbitMQ cluster</p>
              <p><strong>Features:</strong> Async processing, dead letter queues</p>
              <p><strong>Use Cases:</strong> Environment provisioning, AI processing</p>
            </div>
          </div>

          <div class="service-card animate__animated animate__fadeInUp">
            <div class="service-header">
              <i class="fas fa-search"/>
              <h3>Search & Analytics</h3>
            </div>
            <div class="service-content">
              <p><strong>Technology:</strong> Elasticsearch + Kibana</p>
              <p><strong>Features:</strong> Real-time logging, metrics analytics</p>
              <p><strong>Data:</strong> Security events, performance metrics, audit logs</p>
            </div>
          </div>

          <div class="service-card animate__animated animate__fadeInUp">
            <div class="service-header">
              <i class="fab fa-gitlab"/>
              <h3>CI/CD Pipeline</h3>
            </div>
            <div class="service-content">
              <p><strong>Technology:</strong> GitLab CI/CD + Container Registry</p>
              <p><strong>Features:</strong> Automated testing, security scanning</p>
              <p><strong>Deployment:</strong> Blue-green deployments, rollback capability</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- AI/LLM Integration -->
    <section class="ai-integration">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2><i class="fas fa-brain"/> AI/LLM Integration</h2>
          <p>On-premises AI capabilities with privacy-first architecture</p>
        </div>

        <div class="ai-components">
          <div class="ai-card animate__animated animate__fadeInLeft">
            <div class="ai-header">
              <i class="fas fa-robot"/>
              <h3>Local LLM Deployment</h3>
            </div>
            <div class="ai-content">
              <div class="ai-models">
                <div class="model-item">
                  <span class="model-name">CodeLlama 7B</span>
                  <span class="model-purpose">Code completion & review</span>
                </div>
                <div class="model-item">
                  <span class="model-name">Llama 2 70B</span>
                  <span class="model-purpose">General assistance & documentation</span>
                </div>
                <div class="model-item">
                  <span class="model-name">Mistral 7B</span>
                  <span class="model-purpose">Debugging & optimization</span>
                </div>
              </div>
            </div>
          </div>

          <div class="ai-card animate__animated animate__fadeInRight">
            <div class="ai-header">
              <i class="fas fa-microchip"/>
              <h3>GPU Infrastructure</h3>
            </div>
            <div class="ai-content">
              <ul class="gpu-specs">
                <li><strong>GPU Pool:</strong> 16x NVIDIA A100 80GB</li>
                <li><strong>Inference Engine:</strong> vLLM for optimized serving</li>
                <li><strong>Model Loading:</strong> Dynamic allocation based on demand</li>
                <li><strong>Privacy:</strong> No data leaves the infrastructure</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Client Portfolio -->
    <section class="client-portfolio">
      <div class="container">
        <div class="section-header animate__animated animate__fadeInUp">
          <h2><i class="fas fa-building"/> Client Portfolio</h2>
          <p class="restricted-notice">
            <i class="fas fa-exclamation-triangle"/>
            RESTRICTED - Client information classified for security reasons
          </p>
        </div>

        <div class="client-categories">
          <div class="client-card animate__animated animate__fadeInUp">
            <div class="client-header">
              <i class="fas fa-university"/>
              <h3>Government Agencies</h3>
              <span class="classification restricted">RESTRICTED</span>
            </div>
            <div class="client-content">
              <ul class="client-features">
                <li>Municipal services platforms with complex multi-tenancy</li>
                <li>Pension agency systems with enterprise authorization</li>
                <li>Government compliance and security requirements</li>
                <li>24/7 production support with enterprise SLA</li>
              </ul>
            </div>
          </div>

          <div class="client-card animate__animated animate__fadeInUp">
            <div class="client-header">
              <i class="fas fa-industry"/>
              <h3>Enterprise Applications</h3>
              <span class="classification restricted">RESTRICTED</span>
            </div>
            <div class="client-content">
              <ul class="client-features">
                <li>Complex authorization and user management systems</li>
                <li>Multi-application portfolio management</li>
                <li>Enterprise-scale deployments and integrations</li>
                <li>Regulatory compliance and audit requirements</li>
              </ul>
            </div>
          </div>

          <div class="client-card animate__animated animate__fadeInUp">
            <div class="client-header">
              <i class="fas fa-shield-alt"/>
              <h3>Security & Compliance</h3>
              <span class="classification restricted">RESTRICTED</span>
            </div>
            <div class="client-content">
              <ul class="client-features">
                <li>Government-grade security implementations</li>
                <li>Data sovereignty and residency requirements</li>
                <li>Multi-level security clearance support</li>
                <li>Comprehensive audit logging and monitoring</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Security Notice -->
    <section class="security-notice-section">
      <div class="container">
        <div class="security-notice animate__animated animate__fadeInUp">
          <div class="notice-icon">
            <i class="fas fa-shield-alt"/>
          </div>
          <div class="notice-content">
            <h3>Security Classification Notice</h3>
            <p>
              <strong>CONFIDENTIAL:</strong> This technical information is classified and intended for authorized personnel only.
              Our infrastructure serves government agencies, municipal services, and enterprise clients with strict security and compliance requirements.
              Unauthorized disclosure is prohibited.
            </p>
            <div class="security-features">
              <div class="security-item">
                <i class="fas fa-lock"/>
                <span>Data Sovereignty</span>
              </div>
              <div class="security-item">
                <i class="fas fa-certificate"/>
                <span>Compliance Certified</span>
              </div>
              <div class="security-item">
                <i class="fas fa-user-shield"/>
                <span>Access Controlled</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Technical',
  data() {
    return {
      architectureImage: '/media/recraft/diagrams/architecture-overview.png',
      icons: {
        kubernetes: '/media/recraft/icons/kubernetes.svg',
        docker: '/media/recraft/icons/docker.svg',
        mongodb: '/media/recraft/icons/mongodb.svg',
        python: '/media/recraft/icons/python.svg',
        vuejs: '/media/recraft/icons/vuejs.svg',
        gitlab: '/media/recraft/icons/gitlab.svg',
        haproxy: '/media/recraft/icons/haproxy.svg',
        aiBrain: '/media/recraft/icons/ai-brain.svg',
        redis: '/media/recraft/icons/redis.svg',
        prometheus: '/media/recraft/icons/prometheus.svg'
      },
      serviceSuite: [
        { icon: 'fas fa-dharmachakra', title: 'Kubernetes cluster management', body: 'Multi-cluster management with health checks, remediation, resource scaling, pod lifecycle, ConfigMap and Secret handling.' },
        { icon: 'fas fa-brain', title: 'AI deployment intelligence', body: 'Failed deployments analysed, kubectl corrections proposed, secrets masked before anything reaches the model.' },
        { icon: 'fas fa-globe', title: 'DNS and network management', body: 'Automatic DNS records on public and private lanes, integrated with environments and certificates.' },
        { icon: 'fas fa-shield-halved', title: 'Backup and disaster recovery', body: 'Scheduled database and file backups, verified restores, replication between sites.' },
        { icon: 'fas fa-user-lock', title: 'Enterprise authentication', body: 'JWT and RSA tokens, organisations, roles and a permission chain from organisation down to workload.' },
        { icon: 'fas fa-vial', title: 'API documentation and validation', body: 'Every service publishes OpenAPI documentation and a unified response envelope.' }
      ]
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser'])
  },
  mounted() {
    // Log access to technical information
    this.$store.dispatch('logSecurityEvent', {
      type: 'technical_page_access',
      user: this.currentUser?.username,
      timestamp: new Date().toISOString()
    })
  }
}
</script>

<style scoped>
/* Architecture (moved from Home) */
.architecture-section {
  padding: 80px 0;
  background: var(--ds-white);
}

.tech-icons-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem 2rem;
  margin-bottom: 2.5rem;
}
.tech-icon-item { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; }
.tech-icon-img { width: 40px; height: 40px; object-fit: contain; }
.tech-icon-label { font-size: 0.75rem; color: var(--ds-neutral-500); text-transform: capitalize; }

.arch-card {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  background: var(--ds-neutral-50);
  border: 1px solid var(--ds-neutral-200);
  border-radius: var(--ds-radius-xl);
  padding: 2rem;
  margin-bottom: 2.5rem;
}
@media (min-width: 1024px) { .arch-card { grid-template-columns: 1.1fr 1fr; align-items: start; } }
.arch-img { width: 100%; height: auto; border-radius: var(--ds-radius-lg); display: block; }
.arch-content p { color: var(--ds-neutral-700); line-height: 1.7; margin: 0 0 1.25rem; }
.arch-breakdown { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.25rem; }
.breakdown-section h4 { margin: 0 0 0.3rem; font-family: var(--ds-font-display); font-size: 1rem; color: var(--ds-primary-900); display: flex; gap: 0.5rem; align-items: center; }
.breakdown-section h4 i { color: var(--ds-primary-500); }
.breakdown-section p { font-size: 0.9rem; margin: 0; }
.arch-feature-list { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0.5rem; color: var(--ds-neutral-700); font-size: 0.93rem; }
.arch-feature-list i { color: var(--ds-accent); margin-right: 0.4rem; }

.suite-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 640px) { .suite-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1024px) { .suite-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
.suite-card { background: var(--ds-white); border: 1px solid var(--ds-neutral-200); border-radius: var(--ds-radius-lg); padding: 1.25rem; min-width: 0; }
.suite-card > i { color: var(--ds-primary-500); font-size: 1.3rem; margin-bottom: 0.6rem; display: block; }
.suite-card h3 { margin: 0 0 0.4rem; font-family: var(--ds-font-display); font-size: 1.05rem; color: var(--ds-primary-900); }
.suite-card p { margin: 0; color: var(--ds-neutral-700); font-size: 0.92rem; line-height: 1.55; }

.technical-page {
  min-height: 100vh;
  background: #0a2540;
  color: white;
  padding-top: 80px;
}

/* Header Section */
.technical-header {
  padding: 60px 0;
  text-align: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background-image: url('/media/recraft/textures/texture-grid.svg');
  position: relative;
}

.technical-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 37, 64, 0.85);
  z-index: 0;
}

.technical-header .container {
  position: relative;
  z-index: 1;
}

.security-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.5);
  color: #ff6b6b;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.technical-header h1 {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.technical-header p {
  font-size: 1.2rem;
  opacity: 0.8;
  margin-bottom: 2rem;
}

.user-info {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.demo-indicator {
  background: #10b981;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Section Styling */
.infrastructure-section,
.platform-services,
.ai-integration,
.client-portfolio {
  padding: 80px 0;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.section-header p {
  font-size: 1.1rem;
  opacity: 0.8;
  max-width: 600px;
  margin: 0 auto;
}

.restricted-notice {
  color: #1a7fd4;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

/* Infrastructure Cards */
.infrastructure-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.infra-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.3s ease;
}

.infra-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.card-header i {
  font-size: 1.5rem;
  color: #1a7fd4;
}

.card-header h3 {
  font-size: 1.3rem;
  font-weight: 600;
  flex: 1;
}

.classification {
  background: rgba(255, 107, 107, 0.3);
  color: #ff6b6b;
  padding: 0.3rem 0.8rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
  border: 1px solid rgba(255, 107, 107, 0.5);
}

.classification.restricted {
  background: rgba(26, 127, 212, 0.2);
  color: #1a7fd4;
  border-color: rgba(26, 127, 212, 0.4);
}

.spec-list {
  list-style: none;
  padding: 0;
}

.spec-list li {
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  line-height: 1.5;
}

.spec-list li:last-child {
  border-bottom: none;
}

/* Network Topology */
.network-topology {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.network-topology h3 {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #1a7fd4;
}

.vlan-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.vlan-card {
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 15px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.vlan-card strong {
  display: block;
  font-size: 1.1rem;
  color: #1a7fd4;
  margin-bottom: 0.5rem;
}

.vlan-card span {
  display: block;
  opacity: 0.8;
  margin-bottom: 0.5rem;
}

.vlan-card code {
  background: rgba(0, 0, 0, 0.3);
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  color: #51cf66;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.service-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
}

.service-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.service-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.service-header i {
  font-size: 1.8rem;
  color: #1a7fd4;
}

.service-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
}

.service-content p {
  margin-bottom: 1rem;
  line-height: 1.6;
  opacity: 0.9;
}

/* AI Integration */
.ai-components {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.ai-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.3s ease;
}

.ai-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.ai-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.ai-header i {
  font-size: 2rem;
  color: #1a7fd4;
}

.ai-header h3 {
  font-size: 1.4rem;
  font-weight: 600;
}

.ai-models {
  display: grid;
  gap: 1rem;
}

.model-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.model-name {
  font-weight: 600;
  color: #1a7fd4;
}

.model-purpose {
  opacity: 0.8;
  font-size: 0.9rem;
}

.gpu-specs {
  list-style: none;
  padding: 0;
}

.gpu-specs li {
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  line-height: 1.5;
}

.gpu-specs li:last-child {
  border-bottom: none;
}

/* Client Portfolio */
.client-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.client-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.3s ease;
}

.client-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.client-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.client-header i {
  font-size: 1.5rem;
  color: #1a7fd4;
}

.client-header h3 {
  font-size: 1.3rem;
  font-weight: 600;
  flex: 1;
}

.client-features {
  list-style: none;
  padding: 0;
}

.client-features li {
  padding: 0.8rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  line-height: 1.6;
  position: relative;
  padding-left: 1.5rem;
}

.client-features li:last-child {
  border-bottom: none;
}

.client-features li::before {
  content: '\2022';
  color: #1a7fd4;
  font-weight: 600;
  position: absolute;
  left: 0;
}

/* Security Notice Section */
.security-notice-section {
  padding: 60px 0;
  background: rgba(0, 0, 0, 0.3);
}

.security-notice {
  display: flex;
  gap: 2rem;
  background: rgba(26, 127, 212, 0.1);
  border: 2px solid rgba(26, 127, 212, 0.3);
  border-radius: 20px;
  padding: 2.5rem;
  align-items: flex-start;
}

.notice-icon {
  flex-shrink: 0;
}

.notice-icon i {
  font-size: 3rem;
  color: #1a7fd4;
}

.notice-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a7fd4;
}

.notice-content p {
  line-height: 1.7;
  margin-bottom: 2rem;
  opacity: 0.95;
}

.security-features {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
}

.security-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.security-item i {
  color: #1a7fd4;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .infrastructure-grid {
    grid-template-columns: 1fr;
  }

  .ai-components {
    grid-template-columns: 1fr;
  }

  .vlan-group {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }
}

@media (max-width: 768px) {
  .technical-header {
    padding: 40px 0;
  }

  .technical-header h1 {
    font-size: 2.5rem;
  }

  .section-header h2 {
    font-size: 2rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .infrastructure-section,
  .platform-services,
  .ai-integration,
  .client-portfolio {
    padding: 60px 0;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }

  .client-categories {
    grid-template-columns: 1fr;
  }

  .security-notice {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }

  .security-features {
    justify-content: center;
    gap: 1rem;
  }

  .user-info {
    flex-direction: column;
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .technical-header h1 {
    font-size: 2rem;
  }

  .infra-card,
  .service-card,
  .ai-card,
  .client-card {
    padding: 1.5rem;
  }

  .security-notice {
    padding: 2rem;
  }

  .security-features {
    flex-direction: column;
    align-items: center;
  }

  .vlan-group {
    grid-template-columns: 1fr;
  }
}
</style>
